import { forwardRef } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'

import { cn } from '../../lib/cn'
import { useFieldState } from './Field'

export type ControlSize = 'sm' | 'md' | 'lg'
export type ControlTone = 'paper' | 'chrome'

/**
 * Shared by `Input`, `Select` and — through them — every field in the product. `sm` and `md` carry
 * the type roles so a change to `--t-small-size` or `--t-body-size` reaches the fields; `lg` keeps
 * a literal because the scale has no step between `--t-body` (14 px) and `--t-h3` (16 px) for a
 * 44 px control. Same call as `Button`.
 *
 * Note for callers: `.t-body` / `.t-small` / `.t-mono` are all declared outside a cascade layer in
 * `tokens.css`, so they outrank Tailwind's utilities and each other by declaration order. `.t-mono`
 * is declared last, which is why a `mono` field renders at `--t-mono-size` whatever its size prop
 * says — the behaviour before this change too, since `.t-mono` already beat `text-[0.875rem]`.
 */
export const CONTROL_SIZES: Record<ControlSize, string> = {
  sm: 'h-[30px] t-small',
  md: 'h-9 t-body',
  lg: 'h-11 text-[0.9375rem]',
}

/**
 * The shared field skin.
 *
 * It sets `outline-none` so the browser's default ring never shows *underneath* ours, which
 * means **every caller must also apply `ring-focus`** — `.ring-focus:focus-visible` outranks
 * `.outline-none` and restores the brass outline `docs/DESIGN_SYSTEM.md` requires on every
 * interactive element. `Input`, `Textarea` and `Select` all do; a new control that forgets it
 * would be keyboard-invisible.
 */
export const controlSurface = (tone: ControlTone, invalid: boolean): string =>
  cn(
    'w-full rounded-sm border transition-colors outline-none',
    tone === 'chrome'
      ? 'bg-chrome-800 text-chrome-ink border-chrome-600 placeholder:text-chrome-mute'
      : // border-hair measures 1.19–1.33:1 here — fails WCAG 1.4.11's 3:1 non-text minimum, and
        // this is every field's only at-rest boundary (the focus ring only appears once it's
        // already focused). border-ink-faint clears 3:1 and still reads as a quiet hairline.
        'bg-surface text-ink border-ink-faint placeholder:text-ink-faint',
    invalid && 'border-down',
    'disabled:cursor-not-allowed disabled:opacity-55',
  )

// `prefix` is a legacy HTML attribute typed as string — ours is a node, so it is replaced.
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  inputSize?: ControlSize
  tone?: ControlTone
  invalid?: boolean
  /** Sits inside the field, before the text. Decorative — give the field a label. */
  prefix?: ReactNode
  suffix?: ReactNode
  /** Numbers, ids and tokens read as mono with tabular figures. */
  mono?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    inputSize = 'md',
    tone = 'paper',
    invalid,
    prefix,
    suffix,
    mono,
    className,
    id,
    required,
    'aria-describedby': describedBy,
    ...rest
  },
  ref,
) {
  const field = useFieldState()
  const isInvalid = invalid ?? field?.invalid ?? false

  const input = (
    <input
      ref={ref}
      id={id ?? field?.id}
      aria-invalid={isInvalid || undefined}
      aria-describedby={describedBy ?? field?.describedBy}
      required={required ?? field?.required}
      className={cn(
        'ring-focus',
        controlSurface(tone, isInvalid),
        CONTROL_SIZES[inputSize],
        mono && 't-mono',
        prefix ? 'pl-9' : 'pl-3',
        suffix ? 'pr-9' : 'pr-3',
        className,
      )}
      {...rest}
    />
  )

  if (!prefix && !suffix) return input

  return (
    <div className="relative flex items-center">
      {prefix ? (
        <span
          className={cn(
            'pointer-events-none absolute left-3 flex items-center',
            tone === 'chrome' ? 'text-chrome-mute' : 'text-ink-faint',
          )}
          aria-hidden="true"
        >
          {prefix}
        </span>
      ) : null}
      {input}
      {suffix ? (
        // Unlike prefix, suffix isn't aria-hidden — callers put real read text here (a "EUR"
        // unit, say), so it needs ink-mute, not the decorative-only ink-faint.
        <span
          className={cn(
            'absolute right-3 flex items-center',
            tone === 'chrome' ? 'text-chrome-mute' : 'text-ink-mute',
          )}
        >
          {suffix}
        </span>
      ) : null}
    </div>
  )
})
